import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGuestAuth } from '@/common/contexts/GuestAuthContext';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { checkActive } from '@/api/query/customer';
import AuthForm from './AuthForm/AuthForm';
import { AUTH_STEPS } from './AuthForm/constants/authForm.constants';
import {
	AuthCard,
	AuthClose,
	AuthContent,
	AuthHeader,
	AuthHint,
	AuthMain,
	AuthOverlay,
	AuthSwitchLink,
	AuthSwitchRow,
	AuthTitle,
	AuthVisual,
	AuthVisualCopy,
} from './authModal.styles';

import { STUDIO_HERO_IMAGE } from '@/common/constants/studioPublicMedia';

const AuthModal = ({ tab = 'register', onClose }) => {
	const navigate = useNavigate();
	const { refreshAuthState, authReturnPath, clearAuthReturnPath, openPlanPaywall } =
		useGuestAuth();
	const t = useLanguageData('authModal');
	const translations = useLanguageData('login');
	const [activeTab, setActiveTab] = useState(tab);
	const [formStep, setFormStep] = useState(AUTH_STEPS.EMAIL);

	useEffect(() => {
		setActiveTab(tab);
		setFormStep(AUTH_STEPS.EMAIL);
	}, [tab]);

	const handleStepChange = (step) => {
		setFormStep(step);

		if (step === AUTH_STEPS.PASSWORD_LOGIN || step === AUTH_STEPS.PASSWORD_SET) {
			setActiveTab('login');
			return;
		}

		if (step === AUTH_STEPS.PASSWORD_REGISTER) {
			setActiveTab('register');
		}
	};

	const handleSuccess = async () => {
		const currentPath = `${window.location.pathname}${window.location.search}`;
		const destination = authReturnPath ?? currentPath;
		const isStudioAuthFlow = destination.startsWith('/studio');

		await refreshAuthState();
		onClose();
		clearAuthReturnPath();

		if (isStudioAuthFlow) {
			try {
				const { data } = await checkActive();
				const skipPlanGate =
					new URLSearchParams(window.location.search).get('autoGenerate') ===
					'1';

				if (!data?.isActive && !skipPlanGate) {
					openPlanPaywall(destination);
					return;
				}
			} catch {
				const skipPlanGate =
					new URLSearchParams(window.location.search).get('autoGenerate') ===
					'1';

				if (!skipPlanGate) {
					openPlanPaywall(destination);
					return;
				}
			}
		}

		if (destination !== currentPath) {
			navigate(destination);
		}
	};

	const isRegister = activeTab === 'register';
	const isPasswordStep = formStep !== AUTH_STEPS.EMAIL;

	const getHeaderHint = () => {
		if (isPasswordStep) {
			if (formStep === AUTH_STEPS.PASSWORD_LOGIN) {
				return translations.subtitleLogin;
			}

			if (formStep === AUTH_STEPS.PASSWORD_SET) {
				return translations.subtitleSetPassword;
			}

			return translations.subtitleRegister;
		}

		return isRegister ? t.registerHint : t.loginHint;
	};

	return (
		<AuthOverlay onClick={onClose}>
			<AuthCard onClick={(event) => event.stopPropagation()}>
				<AuthVisual aria-hidden="true">
					<img src={STUDIO_HERO_IMAGE} alt="" />
					<AuthVisualCopy>
						<h2>{isRegister ? t.visualTitle : t.loginVisualTitle}</h2>
						<p>{isRegister ? t.visualDescription : t.loginVisualDescription}</p>
					</AuthVisualCopy>
				</AuthVisual>
				<AuthContent>
					<AuthClose type="button" onClick={onClose} aria-label={t.close}>
						×
					</AuthClose>
					<AuthHeader>
						<AuthTitle>{isRegister ? t.registerTitle : t.loginTitle}</AuthTitle>
						<AuthHint>{getHeaderHint()}</AuthHint>
					</AuthHeader>
					<AuthMain>
						<AuthForm
							mode={activeTab}
							onSuccess={handleSuccess}
							onStepChange={handleStepChange}
						/>
						<AuthSwitchRow>
							{isRegister ? t.hasAccountPrompt : t.noAccountPrompt}
							<AuthSwitchLink
								type="button"
								onClick={() => {
									handleStepChange(AUTH_STEPS.EMAIL);
									setActiveTab(isRegister ? 'login' : 'register');
								}}
							>
								{isRegister ? t.switchToLogin : t.switchToRegister}
							</AuthSwitchLink>
						</AuthSwitchRow>
					</AuthMain>
				</AuthContent>
			</AuthCard>
		</AuthOverlay>
	);
};

export default AuthModal;
